import { Flex, Text } from "@chakra-ui/react";
import { useLoading } from "components/GlobalLoading";
import {
  DespesaQuery,
  RelatorioDespesaItem,
  RelatorioDespesaFields,
} from "features/relatorios/typings";
import NewTable from "components/NewTable";
import useDespesasColumns from "./hooks/useDespesasColumns";
import HeaderTable from "./components/HeaderTable";
import { formatMoney } from "infra/helpers/number";
import { useState, useEffect, useCallback } from "react";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import { useDespesasPdf } from "core/features/relatorios/hooks/useDespesasPdf";
import PDFRelatorioDespesa from "./PDFRelatorio";
import {
  exportExcelRelatorioDespesas,
  getRelatorioDespesasComAnexosBlob,
} from "./util/helper";
import { MdPictureAsPdf } from "react-icons/md";
import colors from "core/resources/theme/colors";
import { SiMicrosoftexcel } from "react-icons/si";
import { PaginateResult } from "core/resources/types/shared";
import { showError } from "infra/helpers/alerta";
import { downloadLocalBlob } from "infra/helpers/download";
import moment from "moment";
import { AssinaturasItem } from "components/Relatorio/FormAssinaturas";

interface RelatorioDemonstrativoDespesasProps {
  onChangeFiltro: (v: Partial<DespesaQuery>) => void;
  total?: number;
  filtro: DespesaQuery;
  data: PaginateResult<RelatorioDespesaItem> | undefined;
  isLoading?: boolean;
}

function RelatorioDemonstrativoDespesas({
  filtro,
  data,

  isLoading,
  onChangeFiltro,
  total,
}: RelatorioDemonstrativoDespesasProps) {
  useLoading(!!isLoading);

  const columns = useDespesasColumns();

  const [selecteds, setSelecteds] = useState<RelatorioDespesaItem[]>([]);

  const [exportType, setExportType] = useState<
    { type: "pdf" | "excel" } | undefined
  >();
  const [modalSelectFieldsOpen, setModalSelectFieldsOpen] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | undefined>();

  const onUnselect = () => onChangeFiltro?.({ pago: undefined });
  const { mutate: handleRelatorioDespesa, isLoading: isLoadingDataPdf } =
    useDespesasPdf({
      ...filtro,
      despesasIds: selecteds.length
        ? selecteds.map((item) => item.id)
        : undefined,
    });

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Relatório de Despesas.pdf`,
    Component: PDFRelatorioDespesa,
    loading: isLoadingDataPdf,
  });

  const _onExportPdf = useCallback(
    async (
      selectedFields: RelatorioDespesaFields[],
      assinaturas?: AssinaturasItem[]
    ) => {
      handleRelatorioDespesa({} as any, {
        onSuccess: async (data) => {
          if (selectedFields?.includes("anexos")) {
            try {
              const onProgressGenerate = (i: number, total: number) =>
                setExportMessage(
                  `Exportando relatório... Progresso: ${i}/${total}`
                );

              const onProgressMerge = (i: number, total: number) =>
                setExportMessage(
                  `Unindo relatórios...\nProgresso: ${i + 1} de ${total}`
                );

              const blob = await getRelatorioDespesasComAnexosBlob(
                { ...data, assinaturas },
                selectedFields,
                onProgressGenerate,
                onProgressMerge
              );

              setExportMessage("Gerando pdf...");

              downloadLocalBlob(
                blob,
                `Relatório de Despesas com Anexos - ${moment().format(
                  "DD_MM_YYYY"
                )}.pdf`,
                "application/pdf"
              );
            } catch (err) {
              console.log(err);
              showError((err as any).message);
            } finally {
              setExportMessage(undefined);
            }
          } else {
            onExportPdf({ ...data, selectedFields, assinaturas });
          }
        },
      });
    },
    [handleRelatorioDespesa, onExportPdf]
  );

  const _onExportExcel = useCallback(
    async (selectedFields: RelatorioDespesaFields[]) => {
      handleRelatorioDespesa({} as any, {
        onSuccess: (data) => {
          exportExcelRelatorioDespesas({
            items: data.items ?? [],
            filtro: filtro!,
            selectedFields,
          });
        },
      });
    },
    [filtro, handleRelatorioDespesa]
  );

  useEffect(() => {
    if (exportType) {
      setModalSelectFieldsOpen(true);
    }
  }, [exportType]);

  useLoading(!!exportMessage, exportMessage);

  return (
    <>
      <NewTable
        title={() => (
          <HeaderTable
            value={filtro.search || ""}
            onSearch={(v) => onChangeFiltro({ search: v })}
            pago={filtro.pago}
            onSelectPago={(v) => onChangeFiltro({ pago: v })}
            _onExportExcel={_onExportExcel}
            _onExportPdf={_onExportPdf}
            modalSelectFieldsOpen={modalSelectFieldsOpen}
            onUnselect={onUnselect}
            setExportType={setExportType}
            setModalSelectFieldsOpen={setModalSelectFieldsOpen}
            exportType={exportType}
          />
        )}
        loading={isLoading}
        columns={columns}
        dataSource={data?.items ?? []}
        customPagination={{
          currentPage: data?.meta.currentPage ?? 0,
          totalItems: data?.meta.totalItems ?? 0,
          itemsPerPage: data?.meta.itemsPerPage ?? 0,
          onChangePage: (p, perPage) => onChangeFiltro?.({ page: p, limit: perPage }),
        }}
        CustomFooter={
          Number.isFinite(total) ? (
            <Flex
              py={4}
              px={4}
              justifyContent="space-between"
              roundedBottom="16px"
              bg="white"
            >
              <Text fontWeight="bold">Total</Text>

              <Text fontWeight="bold">{formatMoney(total)}</Text>
            </Flex>
          ) : null
        }
        customSelectedRows={{
          handler: setSelecteds,
          selecteds,
          actions: [
            {
              title: "Exportar PDF",
              onClick: () => setExportType({ type: "pdf" }),
              icon: <MdPictureAsPdf color={colors.primary} size={20} />,
            },
            {
              title: "Exportar Excel",
              onClick: () => setExportType({ type: "excel" }),
              icon: <SiMicrosoftexcel color={colors.primary} size={20} />,
            },
          ],
        }}
      />
    </>
  );
}

export default RelatorioDemonstrativoDespesas;
